/** Saved views of a table: what a view stores, when it has changed, and where it lives.
 *
 * A view is a name over **four things and nothing else** - the filters (search included), the sort
 * levels, the hidden columns and the grouping (plus the extra pinned columns). It never stores rows,
 * a selection or a scroll position, so opening a view cannot show a figure the table would not have
 * shown. The shipped starter views are `locked`: they cannot be renamed, overwritten or deleted, only
 * duplicated into one that can.
 *
 * **Where they live.** In this browser, per signed-in person and per place (`storage.ts`). An
 * account-level store needs an endpoint that does not exist; `shell.table-views` says so.
 */

import type { FilterState } from "../filters";
import type { SortState } from "../sortRows";
import { readJson, tableKey, writeJson } from "./storage";

export interface ViewConfig {
  filters: FilterState;
  sorts: readonly SortState[];
  hidden: readonly string[];
  groupBy: string | null;
  pinned: readonly string[];
}

export interface SavedView {
  id: string;
  name: string;
  /** A shipped starter view: never edited, renamed or deleted. */
  locked?: boolean;
  config: ViewConfig;
}

/** The id of the implicit first tab: no filters, no sort, nothing hidden. Always present, always locked. */
export const ALL_VIEW = "all";

export const EMPTY_CONFIG: ViewConfig = {
  filters: { search: "", values: {} },
  sorts: [],
  hidden: [],
  groupBy: null,
  pinned: [],
};

/** A canonical form, so two configs that mean the same thing compare equal whatever order a set or a
 * filter map was built in. */
export function canonical(config: ViewConfig): string {
  const values = Object.entries(config.filters.values)
    .filter(([, list]) => list.length > 0)
    .map(([id, list]) => [id, [...list].sort()] as const)
    .sort((a, b) => a[0].localeCompare(b[0]));
  return JSON.stringify({
    q: config.filters.search.trim(),
    v: values,
    s: config.sorts.map((s) => [s.columnId, s.direction]),
    h: [...config.hidden].sort(),
    g: config.groupBy,
    p: [...config.pinned].sort(),
  });
}

export function sameConfig(a: ViewConfig, b: ViewConfig): boolean {
  return canonical(a) === canonical(b);
}

interface Stored {
  views: SavedView[];
  defaultId: string | null;
}

const EMPTY_STORE: Stored = { views: [], defaultId: null };

function isStored(value: unknown): value is Stored {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as Stored).views) &&
    ((value as Stored).defaultId === null || typeof (value as Stored).defaultId === "string")
  );
}

export function readViews(userKey: string | undefined, place: string): Stored {
  const stored = readJson<unknown>(tableKey("views", userKey, place), EMPTY_STORE);
  return isStored(stored) ? { views: stored.views.filter((v) => !v.locked), defaultId: stored.defaultId } : EMPTY_STORE;
}

export function writeViews(userKey: string | undefined, place: string, store: Stored): void {
  writeJson(tableKey("views", userKey, place), store);
}

let counter = 0;
/** A view id: unique in this browser. Not a security value. */
export function newViewId(): string {
  counter += 1;
  return `v${Date.now().toString(36)}${counter.toString(36)}`;
}
